import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Send } from "lucide-react";
import { Section, Field } from "./_layout";

const getTelegramSettings = () => api.get("/settings/admin/telegram").then(r => r.data);
const saveTelegramSettings = (body) => api.post("/settings/admin/telegram", body).then(r => r.data);

export default function TelegramTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  
  const { data, isLoading } = useQuery({ 
    queryKey: ["admin-telegram"], 
    queryFn: getTelegramSettings 
  });
  
  const [form, setForm] = useState({
    token: "",
    username: "",
    channel_id: "",
    invite_link: "",
    enable_popup: false
  });

  useEffect(() => {
    if (data) {
      setForm({
        token: data.tail ? `••••••••••••${data.tail}` : "",
        username: data.username || "",
        channel_id: data.channel_id || "",
        invite_link: data.invite_link || "",
        enable_popup: data.enable_popup || false
      });
    }
  }, [data]);

  const patchMut = useMutation({
    mutationFn: (body) => saveTelegramSettings(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-telegram"] });
      toast({ title: "Telegram settings saved" });
    },
    onError: (err) => {
      toast({ title: "Error saving settings", description: err.response?.data?.detail || err.message, variant: "destructive" });
    }
  });

  if (isLoading) return <Loader2 className="size-5 animate-spin text-white/50" />;

  const upd = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Section title="Bot Configuration">
        <Field label="Bot Token (BotFather)">
          <input type="text" value={form.token} onChange={(e) => upd("token", e.target.value)}
            placeholder="123456789:ABCdefGHIjkl..."
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="Bot Username">
          <input type="text" value={form.username} onChange={(e) => upd("username", e.target.value)}
            placeholder="@DaddyofAI_bot"
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm" />
        </Field>
      </Section>

      <Section title="Channel & Invites">
        <Field label="Channel ID (for auto-broadcasts)">
          <input type="text" value={form.channel_id} onChange={(e) => upd("channel_id", e.target.value)}
            placeholder="-10012345678"
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="Invite Link (for user popups)">
          <input type="text" value={form.invite_link} onChange={(e) => upd("invite_link", e.target.value)}
            placeholder="https://t.me/+AbCdEfGhIj"
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm" />
        </Field>
        <label className="flex items-center gap-2 mt-4 cursor-pointer">
          <input type="checkbox" checked={form.enable_popup} onChange={(e) => upd("enable_popup", e.target.checked)}
            className="rounded bg-ink-800 border-white/10 text-amber-500 focus:ring-amber-500" />
          <span className="text-sm font-medium">Show Join Popup to non-linked users</span>
        </label>
      </Section>

      <button
        onClick={() => patchMut.mutate(form)}
        disabled={patchMut.isPending}
        className="lg:col-span-2 bg-gold-grad text-ink-900 px-4 py-2 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-1 mt-4"
      >
        {patchMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
        Save Telegram Configuration
      </button>
    </div>
  );
}